import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, Download, Trash2 } from 'lucide-react';
import { motion } from 'framer-motion';
import SiteHeader from '../components/SiteHeader';
import FooterExpanded from '../components/landing/FooterExpanded';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function MyMomentsPage() {
  const { token } = useParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Forget me: deletes this results link now (no face data is ever stored).
  const forget = async () => {
    if (!window.confirm('Delete this private link and its list of photos now?')) return;
    try {
      await axios.delete(`${API_URL}/api/my-moments/${token}`);
      setItems([]);
      setError('Deleted. This link no longer works, and no face data of yours is stored.');
    } catch (err) {
      alert(err.response?.data?.detail || 'Could not delete this link. Please try again.');
    }
  };

  useEffect(() => {
    axios.get(`${API_URL}/api/my-moments/${token}`)
      .then(({ data }) => setItems(data.items || []))
      .catch((err) => setError(err.response?.data?.detail || 'This link is invalid or has expired.'))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center">
        <Sparkles className="w-8 h-8 text-champagne animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-midnight flex items-center justify-center p-4">
        <div className="aura-card rounded-2xl p-8 text-center max-w-md">
          <h1 className="text-xl font-serif font-medium text-text-light mb-2">Link Unavailable</h1>
          <p className="text-text-muted mb-6">{error}</p>
          <Link to="/" className="text-champagne hover:underline">Return to Memouraa</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-champagne" />
            <span className="font-serif text-text-light">My Moments</span>
          </div>
          <div className="flex items-center gap-3">
            <p className="text-text-muted text-sm">{items.length} {items.length === 1 ? 'photo' : 'photos'}</p>
            <button onClick={forget} className="btn-outline text-xs flex items-center gap-1 text-red-400 border-red-400/30 hover:border-red-400" data-testid="my-moments-forget">
              <Trash2 className="w-3 h-3" /> Forget me
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {items.map(m => (
            <a key={m.id} href={`${API_URL}${m.url}`} target="_blank" rel="noopener noreferrer" className="aura-card rounded-xl overflow-hidden block hover:scale-[1.02] transition-transform">
              {m.file_type === 'video' ? (
                <video src={`${API_URL}${m.url}`} className="w-full aspect-square object-cover" muted />
              ) : (
                <img src={`${API_URL}${m.url}`} alt={`By ${m.guest_name}`} className="w-full aspect-square object-cover" />
              )}
              <div className="flex items-center justify-between p-2">
                <span className="text-xs text-text-muted truncate">By {m.guest_name}</span>
                <Download className="w-3 h-3 text-champagne" />
              </div>
            </a>
          ))}
        </motion.div>
      </div>
      <FooterExpanded />
    </div>
  );
}
